import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Grid } from '@mui/material';
import {
  assignUserToProject,
  editUserRoleInProject,
} from '../../modules/project';
import { listAllUsers } from '../../modules/users';
import Modal from '../../ui/layout/Modal';
import CustomSelect from '../../ui/primitives/CustomSelect';
import AutocompleteField from '../../ui/primitives/AutocompleteField ';

const ROLE_OPTIONS = [
  { id: 'member', name: 'Team Member' },
  { id: 'admin', name: 'Project Admin' },
  { id: 'executive-manager', name: 'Executive Manager' },
];

const roleToUserRolesMap = {
  member: ['team-member'],
  admin: ['team-member'],
  'executive-manager': ['executive-manager'],
};

const AssignRole = ({
  currentProject,
  open,
  setOpen,
  fetchUsersData,
  associatedUsers,
  mode,
  selectedUser,
  setMode,
}) => {
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [role, setRole] = useState('member');
  const [selectedValues, setSelectedValues] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const fetchUsers = async () => {
    const data = await listAllUsers(null, null, null, 'exclude-inactive');
    setUsers(data?.users ?? []);
  };

  useEffect(() => {
    fetchUsers();
    if (mode === 'edit') setRole(selectedUser?.role);
  }, [open]);
  const handleClose = () => {
    setOpen(false);
    setError(null);
    setSelectedValues([]);
    setMode('add');
    setRole('member');
  };

  const handleEditSubmit = async (event) => {
    event.preventDefault();
    try {
      await editUserRoleInProject({ user_id: selectedUser?.id, role });
      handleClose();
      fetchUsersData();
    } catch (err) {
      setError(err.message || 'Failed to update role. Please try again.');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedValues.length) {
      setError('Please select at least one user.');
      return;
    }
    const payload = selectedValues.map((user) => ({
      user_id: user.id,
      role,
      project_id: currentProject.id,
    }));
    try {
      await assignUserToProject(payload);
      handleClose();
      fetchUsersData();
    } catch (err) {
      setError(err.message || 'Failed to update project. Please try again.');
    }
  };
  const userOptions = useMemo(() => {
    const allowedRoles = roleToUserRolesMap[role] ?? [];
    const associatedIds = new Set(
      associatedUsers?.map((au) => au?.user_id ?? au?.id).filter(Boolean)
    );

    return users
      .filter(
        (user) =>
          allowedRoles.includes(user.role) &&
          !associatedIds.has(user.id)
      )
      .map((user) => ({
        id: user.id,
        name: `${user.first_name} ${user.last_name}`,
        status: user.status || '',
      }));
  }, [users, associatedUsers, role]);

  const formId = 'assign-role-form';

  return (
    <Modal
      open={open}
      modalTitle="Manage Team"
      handleClose={handleClose}
      width="sm"
      action={() => (
        <>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="submit" form={formId} variant="contained">
            {mode === 'edit' ? 'Save' : 'Assign'}
          </Button>
        </>
      )}
    >
      <Box
        id={formId}
        component="form"
        noValidate
        onSubmit={mode === 'edit' ? handleEditSubmit : handleSubmit}
      >
        <Grid container spacing={2}>
          {mode === 'edit' && (
            <Grid item xs={12}>
              <CustomSelect
                label="User"
                value={selectedUser?.id ?? ''}
                options={[{ id: selectedUser?.id, name: selectedUser?.name }]}
                onChange={() => { }}
                disabled
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <CustomSelect
              id="role"
              label="Role"
              required
              value={role}
              options={ROLE_OPTIONS}
              onChange={(val) => {
                setRole(val);
                setSelectedValues([]);
              }}
            />
          </Grid>
          {mode === 'add' && (
            <Grid item xs={12}>
              <AutocompleteField
                id="users"
                label="Users"
                required
                multiple
                value={selectedValues}
                onChange={setSelectedValues}
                inputValue={inputValue}
                onInputChange={setInputValue}
                options={userOptions}
                placeholder="Users"
                filterSelectedOptions
                error={error && !selectedValues.length ? error : undefined}
                renderOption={(option) => (
                  <>
                    <span>{option.name}</span>
                    <span style={{ fontSize: '0.75rem', color: '#888', marginLeft: 6 }}>
                      ({option.status})
                    </span>
                  </>
                )}
              />
            </Grid>
          )}
        </Grid>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Box>
    </Modal>
  );
};

export default AssignRole;
